import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  ClipboardList,
  FileText,
  MapPin,
  MessageSquare,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { ConfirmModal, Pagination } from '@tutor/ui';
import Modal from '@/components/Modal/Modal';
import { CaseListCard } from '@/components/common/CaseListCard';
import TagBar from '@/components/common/TagBar';
import { formatEducationSystemLevel } from '@tutor/types';
import { useAuthContext } from '@/contexts/useAuthContext';
import { useDeleteApplicationMutation, useMyApplications } from '@/hooks/api/useApplication';
import { useMyShortlists, useRespondToShortlistMutation } from '@/hooks/api/useShortlist';
import { useCaseDetail } from '@/hooks/api/useCaseDetail';
import { useI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';
import { Link } from '@/i18n/navigation';
import type {
  ApplicationData,
  CaseData,
  MyTutorShortlistCaseData,
  TutorShortlistData,
  TutorShortlistRespondRequest,
} from '@/types/api';

type PrimaryTab = 'open' | 'matched' | 'closed';
type OpenFilter = 'all' | 'applied' | 'recommended' | 'action_needed';
type TutorViewSource = 'applied' | 'recommended';
type TutorProgressKey =
  | 'under_review'
  | 'awaiting_response'
  | 'accepted_by_you'
  | 'matched'
  | 'not_selected'
  | 'declined_by_you'
  | 'no_longer_available';

type ApplicationWithCase = ApplicationData & {
  case?: {
    id: string;
    tuitionType: CaseData['tuitionType'];
    educationSystem?: string;
    subject: string;
    level: CaseData['level'];
    status: CaseData['status'];
    teachingLanguages: string[];
    lessonsPerWeek: number;
    lessonDuration: number;
    state: string | null;
    district?: string | null;
    areaNote?: string | null;
    hourlyRate?: number;
    matchedTutorId?: string | null;
    closedAt?: string | null;
  };
};

type PageItem = {
  id: string;
  itemKey: string;
  source: TutorViewSource;
  primaryTab: PrimaryTab;
  progressKey: TutorProgressKey;
  progressLabel: string;
  sourceLabel: string;
  sourceBadgeClassName: string;
  progressBadgeClassName: string;
  sortDate: string;
  caseId: string;
  caseSummary?: ApplicationWithCase['case'] | MyTutorShortlistCaseData;
  application?: ApplicationWithCase;
  shortlist?: TutorShortlistData;
  canWithdraw: boolean;
  canRespond: boolean;
  isUnavailable: boolean;
  actionNeeded: boolean;
  supportingText: string;
};

const PRIMARY_TABS: Array<{ id: PrimaryTab; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'matched', label: 'Matched' },
  { id: 'closed', label: 'Closed' },
];

const OPEN_FILTERS: Array<{ id: OpenFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'applied', label: 'Applied' },
  { id: 'recommended', label: 'Recommended' },
  { id: 'action_needed', label: 'Action Needed' },
];

const PROGRESS_STYLE: Record<TutorProgressKey, string> = {
  under_review: 'bg-amber-100 text-amber-800',
  awaiting_response: 'bg-blue-100 text-blue-800',
  accepted_by_you: 'bg-emerald-100 text-emerald-800',
  matched: 'bg-green-100 text-green-800',
  not_selected: 'bg-red-100 text-red-800',
  declined_by_you: 'bg-gray-200 text-gray-700',
  no_longer_available: 'bg-gray-200 text-gray-700',
};

const PAGE_SIZE = 10;
const FETCH_LIMIT = 50;

const SkeletonCard: React.FC = () => (
  <div className="animate-pulse rounded-xl border border-gray-200 bg-white p-5">
    <div className="mb-3 flex justify-between">
      <div className="space-y-2">
        <div className="h-4 w-32 rounded bg-gray-200" />
        <div className="h-3 w-24 rounded bg-gray-100" />
      </div>
      <div className="h-6 w-20 rounded-full bg-gray-100" />
    </div>
    <div className="mb-1 h-3 w-full rounded bg-gray-100" />
    <div className="h-3 w-2/3 rounded bg-gray-100" />
  </div>
);

const toCaseData = (
  item: ApplicationWithCase['case'] | MyTutorShortlistCaseData | undefined,
  fallbackId: string,
  createdAt: string,
  updatedAt: string
): CaseData => ({
  id: item?.id ?? fallbackId,
  parentProfileId: '',
  tuitionType: item?.tuitionType === 'both' ? 'physical' : item?.tuitionType ?? 'physical',
  educationSystem: item?.educationSystem,
  level: item?.level ?? 'form_1',
  subject: item?.subject ?? 'Unknown Subject',
  teachingLanguages: item?.teachingLanguages ?? [],
  lessonsPerWeek: item?.lessonsPerWeek ?? 0,
  lessonDuration: item?.lessonDuration ?? 0,
  tutorRemarks: '',
  state: item?.state ?? '',
  district: item?.district ?? '',
  areaNote: item?.areaNote ?? '',
  preferredTimes: {},
  status: 'new',
  rejectionReason: null,
  viewCount: 0,
  applicationCount: 0,
  matchedTutorId: null,
  matchedAt: null,
  createdAt,
  updatedAt,
  closedAt: null,
  hourlyRate: item?.hourlyRate,
});

const compareItems = (a: PageItem, b: PageItem) => {
  const aPriority = a.actionNeeded ? 2 : a.primaryTab === 'open' ? 1 : 0;
  const bPriority = b.actionNeeded ? 2 : b.primaryTab === 'open' ? 1 : 0;

  if (aPriority !== bPriority) {
    return bPriority - aPriority;
  }

  return new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime();
};

const isCaseUnavailable = (
  caseSummary: ApplicationWithCase['case'] | MyTutorShortlistCaseData | undefined
) => !caseSummary || caseSummary.status === 'closed' || !!caseSummary.closedAt;

const isMatchedToTutor = (
  caseSummary: ApplicationWithCase['case'] | MyTutorShortlistCaseData | undefined,
  tutorProfileId?: string
) => !!caseSummary && !!tutorProfileId && caseSummary.matchedTutorId === tutorProfileId;

const buildApplicationItem = (
  application: ApplicationWithCase,
  tutorProfileId?: string,
  isZh = false
): PageItem => {
  const caseSummary = application.case;
  const unavailable = isCaseUnavailable(caseSummary);

  if (application.status === 'accepted' || isMatchedToTutor(caseSummary, tutorProfileId)) {
    return {
      id: application.id,
      itemKey: `application:${application.id}`,
      source: 'applied',
      primaryTab: 'matched',
      progressKey: 'matched',
      progressLabel: isZh ? '已配对' : 'Matched',
      sourceLabel: isZh ? '已申请' : 'Applied',
      sourceBadgeClassName: 'bg-gray-100 text-gray-700',
      progressBadgeClassName: PROGRESS_STYLE.matched,
      sortDate: application.updatedAt,
      caseId: caseSummary?.id ?? application.caseId,
      caseSummary,
      application,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: false,
      actionNeeded: false,
      supportingText: isZh ? '你已申请此个案，并已成功配对给你。' : 'You applied to this case and it has been matched to you.',
    };
  }

  if (application.status === 'rejected') {
    return {
      id: application.id,
      itemKey: `application:${application.id}`,
      source: 'applied',
      primaryTab: 'closed',
      progressKey: 'not_selected',
      progressLabel: isZh ? '未被选中' : 'Not selected',
      sourceLabel: isZh ? '已申请' : 'Applied',
      sourceBadgeClassName: 'bg-gray-100 text-gray-700',
      progressBadgeClassName: PROGRESS_STYLE.not_selected,
      sortDate: application.updatedAt,
      caseId: caseSummary?.id ?? application.caseId,
      caseSummary,
      application,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: false,
      actionNeeded: false,
      supportingText: isZh ? '此申请未继续推进。' : 'This application did not move forward.',
    };
  }

  if (unavailable) {
    return {
      id: application.id,
      itemKey: `application:${application.id}`,
      source: 'applied',
      primaryTab: 'closed',
      progressKey: 'no_longer_available',
      progressLabel: isZh ? '已不可申请' : 'No longer available',
      sourceLabel: isZh ? '已申请' : 'Applied',
      sourceBadgeClassName: 'bg-gray-100 text-gray-700',
      progressBadgeClassName: PROGRESS_STYLE.no_longer_available,
      sortDate: application.updatedAt,
      caseId: caseSummary?.id ?? application.caseId,
      caseSummary,
      application,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: true,
      actionNeeded: false,
      supportingText: isZh ? '此个案已关闭或不再开放。' : 'This case has already been closed or is no longer available.',
    };
  }

  return {
    id: application.id,
    itemKey: `application:${application.id}`,
    source: 'applied',
    primaryTab: 'open',
    progressKey: 'under_review',
    progressLabel: isZh ? '审核中' : 'Under review',
    sourceLabel: isZh ? '已申请' : 'Applied',
    sourceBadgeClassName: 'bg-gray-100 text-gray-700',
    progressBadgeClassName: PROGRESS_STYLE.under_review,
    sortDate: application.createdAt,
    caseId: caseSummary?.id ?? application.caseId,
    caseSummary,
    application,
    canWithdraw: application.status === 'pending',
    canRespond: false,
    isUnavailable: false,
    actionNeeded: false,
    supportingText: isZh ? '你已申请此个案，我们正在审核中。' : 'You applied to this case and we are reviewing it.',
  };
};

const buildShortlistItem = (
  shortlist: TutorShortlistData,
  tutorProfileId?: string,
  isZh = false
): PageItem => {
  const caseSummary = shortlist.case;
  const unavailable = isCaseUnavailable(caseSummary);
  const matched = isMatchedToTutor(caseSummary, tutorProfileId);

  if (matched) {
    return {
      id: shortlist.id,
      itemKey: `shortlist:${shortlist.id}`,
      source: 'recommended',
      primaryTab: 'matched',
      progressKey: 'matched',
      progressLabel: isZh ? '已配对' : 'Matched',
      sourceLabel: isZh ? '推荐' : 'Recommended',
      sourceBadgeClassName: 'bg-indigo-100 text-indigo-700',
      progressBadgeClassName: PROGRESS_STYLE.matched,
      sortDate: shortlist.updatedAt,
      caseId: caseSummary?.id ?? shortlist.caseId,
      caseSummary,
      shortlist,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: false,
      actionNeeded: false,
      supportingText: isZh ? 'Tutor Founder 推荐了此个案，现已成功配对给你。' : 'Tutor Founder recommended this case and it has been matched to you.',
    };
  }

  if (shortlist.status === 'declined') {
    return {
      id: shortlist.id,
      itemKey: `shortlist:${shortlist.id}`,
      source: 'recommended',
      primaryTab: 'closed',
      progressKey: 'declined_by_you',
      progressLabel: isZh ? '你已拒绝' : 'Declined by you',
      sourceLabel: isZh ? '推荐' : 'Recommended',
      sourceBadgeClassName: 'bg-indigo-100 text-indigo-700',
      progressBadgeClassName: PROGRESS_STYLE.declined_by_you,
      sortDate: shortlist.updatedAt,
      caseId: caseSummary?.id ?? shortlist.caseId,
      caseSummary,
      shortlist,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: false,
      actionNeeded: false,
      supportingText: isZh ? '你已拒绝这个推荐。' : 'You declined this recommendation.',
    };
  }

  if (unavailable) {
    return {
      id: shortlist.id,
      itemKey: `shortlist:${shortlist.id}`,
      source: 'recommended',
      primaryTab: 'closed',
      progressKey: 'no_longer_available',
      progressLabel: isZh ? '已不可申请' : 'No longer available',
      sourceLabel: isZh ? '推荐' : 'Recommended',
      sourceBadgeClassName: 'bg-indigo-100 text-indigo-700',
      progressBadgeClassName: PROGRESS_STYLE.no_longer_available,
      sortDate: shortlist.updatedAt,
      caseId: caseSummary?.id ?? shortlist.caseId,
      caseSummary,
      shortlist,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: true,
      actionNeeded: false,
      supportingText: isZh ? '此个案已关闭或不再开放。' : 'This case has already been closed or is no longer available.',
    };
  }

  if (shortlist.status === 'accepted') {
    return {
      id: shortlist.id,
      itemKey: `shortlist:${shortlist.id}`,
      source: 'recommended',
      primaryTab: 'open',
      progressKey: 'accepted_by_you',
      progressLabel: isZh ? '你已接受' : 'Accepted by you',
      sourceLabel: isZh ? '推荐' : 'Recommended',
      sourceBadgeClassName: 'bg-indigo-100 text-indigo-700',
      progressBadgeClassName: PROGRESS_STYLE.accepted_by_you,
      sortDate: shortlist.updatedAt,
      caseId: caseSummary?.id ?? shortlist.caseId,
      caseSummary,
      shortlist,
      canWithdraw: false,
      canRespond: false,
      isUnavailable: false,
      actionNeeded: false,
      supportingText: isZh ? '你已表示这个个案适合你，但最终仍需家长确认。' : 'You said this case suits you. Final confirmation still depends on the parent.',
    };
  }

  return {
    id: shortlist.id,
    itemKey: `shortlist:${shortlist.id}`,
    source: 'recommended',
    primaryTab: 'open',
    progressKey: 'awaiting_response',
    progressLabel: isZh ? '等待你的回应' : 'Awaiting your response',
    sourceLabel: isZh ? '推荐' : 'Recommended',
    sourceBadgeClassName: 'bg-indigo-100 text-indigo-700',
    progressBadgeClassName: PROGRESS_STYLE.awaiting_response,
    sortDate: shortlist.addedAt,
    caseId: caseSummary?.id ?? shortlist.caseId,
    caseSummary,
    shortlist,
    canWithdraw: false,
    canRespond: true,
    isUnavailable: false,
    actionNeeded: true,
    supportingText: isZh ? 'Tutor Founder 为你推荐了此个案。' : 'Tutor Founder recommended this case for you.',
  };
};

const ApplicationCard: React.FC<{
  item: PageItem;
  onOpenDetails: (itemKey: string, caseId: string) => void;
  onRequestWithdraw: (application: ApplicationWithCase) => void;
  onRequestRespond: (
    shortlist: TutorShortlistData,
    action: TutorShortlistRespondRequest['status']
  ) => void;
}> = ({ item, onOpenDetails, onRequestWithdraw, onRequestRespond }) => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const caseData = toCaseData(item.caseSummary, item.caseId, item.sortDate, item.sortDate);

  return (
    <CaseListCard
      caseData={caseData}
      onViewDetails={() => onOpenDetails(item.itemKey, item.caseId)}
      headerActions={
        <div className="flex flex-wrap justify-end gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${item.sourceBadgeClassName}`}
          >
            {item.sourceLabel}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${item.progressBadgeClassName}`}
          >
            {item.progressLabel}
          </span>
          {item.actionNeeded && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
              {isZh ? '需要处理' : 'Action needed'}
            </span>
          )}
        </div>
      }
      footerActions={
        item.canRespond && item.shortlist ? (
          <>
            <button
              onClick={() => onRequestRespond(item.shortlist!, 'declined')}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
            >
              {isZh ? '拒绝' : 'Reject'}
            </button>
            <button
              onClick={() => onRequestRespond(item.shortlist!, 'accepted')}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
            >
              {isZh ? '接受' : 'Accept'}
            </button>
          </>
        ) : item.canWithdraw && item.application ? (
          <button
            onClick={() => onRequestWithdraw(item.application!)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {isZh ? '撤回' : 'Withdraw'}
          </button>
        ) : (
          <span className="max-w-[220px] text-right text-xs text-gray-400">{item.supportingText}</span>
        )
      }
      hideShareButton
    />
  );
};

const CaseDetailBody: React.FC<{ caseData: CaseData }> = ({ caseData }) => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';

  return (
  <div className="space-y-6">
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-bold">
            {formatEducationSystemLevel(caseData.educationSystem, caseData.level, {
              useShortSystemLabel: true,
            })}{' '}
            - {localizeText(locale, caseData.subject)}
          </h2>
        </div>
        <div className="text-primary text-2xl font-bold">
          RM {caseData.hourlyRate?.toString() || '0'}
          <span className="text-sm font-normal text-gray-600">{isZh ? ' / 小时' : ' / hour'}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
          {localizeText(locale, caseData.subject)}
        </span>
      </div>
    </div>

    <div className="border-t pt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="mb-2 flex items-center text-base font-bold text-gray-800">
            <MapPin className="mr-2 h-4 w-4" />
            {isZh ? '地点' : 'Location'}
          </h4>
          <p className="pl-6 text-gray-600">
            {caseData.district ? `${caseData.district}, ` : ''}
            {localizeText(locale, caseData.state)}
          </p>
        </div>
        <div>
          <h4 className="mb-2 flex items-center text-base font-bold text-gray-800">
            <FileText className="mr-2 h-4 w-4" />
            {isZh ? '授课方式' : 'Tuition Type'}
          </h4>
          <p className="pl-6 text-gray-600">
            {caseData.tuitionType === 'physical'
              ? isZh ? '线下' : 'Physical'
              : caseData.tuitionType === 'online'
                ? isZh ? '线上' : 'Online'
                : isZh ? '线上 + 线下' : 'Online + Physical'}
          </p>
        </div>
      </div>
    </div>

    <div className="border-t pt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="mb-2 flex items-center text-base font-bold text-gray-800">
            <Calendar className="mr-2 h-4 w-4" />
            {isZh ? '频率' : 'Frequency'}
          </h4>
          <p className="pl-6 text-gray-600">
            {isZh ? `每周 ${caseData.lessonsPerWeek} 次，每次 ${caseData.lessonDuration} 小时` : `${caseData.lessonsPerWeek}x per week, ${caseData.lessonDuration} hours each`}
          </p>
        </div>
        <div>
          <h4 className="mb-2 flex items-center text-base font-bold text-gray-800">
            <BookOpen className="mr-2 h-4 w-4" />
            {isZh ? '语言' : 'Languages'}
          </h4>
          <p className="pl-6 text-gray-600">{localizeStringList(locale, caseData.teachingLanguages).join(', ')}</p>
        </div>
      </div>
    </div>

    {caseData.tutorRemarks && (
      <div className="border-t pt-4">
        <h4 className="mb-3 flex items-center text-base font-bold text-gray-800">
          <MessageSquare className="mr-2 h-4 w-4" />
          {isZh ? '补充说明' : 'Additional Remarks'}
        </h4>
        <p className="ml-6 rounded-lg bg-gray-50 p-4 text-gray-600">{caseData.tutorRemarks}</p>
      </div>
    )}
  </div>
  );
};

const MyApplicationsPage: React.FC = () => {
  const { locale } = useI18n();
  const { profile, firebaseUser } = useAuthContext();
  const isZh = locale === 'zh-CN';
  const [selectedPrimaryTab, setSelectedPrimaryTab] = useState<PrimaryTab>('open');
  const [selectedOpenFilter, setSelectedOpenFilter] = useState<OpenFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);
  const [applicationToWithdraw, setApplicationToWithdraw] = useState<ApplicationWithCase | null>(null);
  const [shortlistResponseTarget, setShortlistResponseTarget] = useState<{
    shortlist: TutorShortlistData;
    action: TutorShortlistRespondRequest['status'];
  } | null>(null);

  const { data, isLoading, error } = useMyApplications({ page: 1, limit: FETCH_LIMIT });
  const {
    data: shortlistData,
    isLoading: isShortlistsLoading,
    error: shortlistError,
  } = useMyShortlists({ page: 1, limit: FETCH_LIMIT });
  const withdraw = useDeleteApplicationMutation();
  const respondToShortlist = useRespondToShortlistMutation();
  const { data: caseDetailData, isLoading: isCaseDetailLoading } = useCaseDetail(
    selectedCaseId ?? undefined,
    'direct'
  );

  const applications = useMemo<ApplicationWithCase[]>(
    () => data?.applications ?? [],
    [data?.applications]
  );
  const shortlists = useMemo<TutorShortlistData[]>(
    () => shortlistData?.shortlists ?? [],
    [shortlistData?.shortlists]
  );

  const mergedItems = useMemo<PageItem[]>(() => {
    const tutorProfileId = profile?.id;
    const applicationItems = applications.map((application) =>
      buildApplicationItem(application, tutorProfileId, isZh)
    );
    const shortlistItems = shortlists.map((shortlist) =>
      buildShortlistItem(shortlist, tutorProfileId, isZh)
    );

    return [...applicationItems, ...shortlistItems].sort(compareItems);
  }, [applications, isZh, profile?.id, shortlists]);

  const visibleItems = useMemo(() => {
    const byPrimaryTab = mergedItems.filter((item) => item.primaryTab === selectedPrimaryTab);

    if (selectedPrimaryTab !== 'open') {
      return byPrimaryTab;
    }

    switch (selectedOpenFilter) {
      case 'applied':
        return byPrimaryTab.filter((item) => item.source === 'applied');
      case 'recommended':
        return byPrimaryTab.filter((item) => item.source === 'recommended');
      case 'action_needed':
        return byPrimaryTab.filter((item) => item.actionNeeded);
      case 'all':
      default:
        return byPrimaryTab;
    }
  }, [mergedItems, selectedOpenFilter, selectedPrimaryTab]);

  const totalPages = Math.max(1, Math.ceil(visibleItems.length / PAGE_SIZE));
  const paginatedItems = useMemo(
    () => visibleItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [currentPage, visibleItems]
  );

  const selectedItem = useMemo(
    () => mergedItems.find((item) => item.itemKey === selectedItemKey),
    [mergedItems, selectedItemKey]
  );

  const primaryTabCount = (tab: PrimaryTab) =>
    mergedItems.filter((item) => item.primaryTab === tab).length;

  const openFilterCount = (filter: OpenFilter) => {
    const openItems = mergedItems.filter((item) => item.primaryTab === 'open');

    switch (filter) {
      case 'applied':
        return openItems.filter((item) => item.source === 'applied').length;
      case 'recommended':
        return openItems.filter((item) => item.source === 'recommended').length;
      case 'action_needed':
        return openItems.filter((item) => item.actionNeeded).length;
      case 'all':
      default:
        return openItems.length;
    }
  };

  const hasError = error || shortlistError;
  const errorMessage =
    error instanceof Error
      ? error.message
      : shortlistError instanceof Error
        ? shortlistError.message
        : isZh ? '发生错误' : 'An error occurred';

  if (!firebaseUser || profile?.role !== 'tutor') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <ClipboardList className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <h2 className="mb-2 text-2xl font-bold text-gray-800">{isZh ? '无法访问' : 'Access Denied'}</h2>
          <p className="text-gray-600">{isZh ? '只有导师可以访问此页面。' : 'Only tutors can access this page.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-primary to-secondary py-16 text-white md:py-20">
        <div className="container mx-auto px-4">
          <div className="mb-4 flex items-center gap-3">
            <ClipboardList className="h-8 w-8" />
            <h1 className="text-3xl font-bold md:text-4xl">{isZh ? '我的申请' : 'My Applications'}</h1>
          </div>
          <p className="max-w-2xl text-white/90">
            {isZh ? '追踪你已申请的机会，以及 Tutor Founder 为你推荐的个案。' : 'Track the opportunities you applied for and the cases Tutor Founder recommended to you.'}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 md:py-12">
        <TagBar
          items={PRIMARY_TABS.map((tab) => ({
            id: tab.id,
            label:
              tab.id === 'open'
                ? isZh ? '进行中' : 'Open'
                : tab.id === 'matched'
                  ? isZh ? '已配对' : 'Matched'
                  : isZh ? '已关闭' : 'Closed',
            count: primaryTabCount(tab.id),
          }))}
          activeId={selectedPrimaryTab}
          onChange={(tab) => {
            setSelectedPrimaryTab(tab);
            setSelectedOpenFilter('all');
            setCurrentPage(1);
          }}
          className="mb-4 border-b border-gray-200"
          innerClassName="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0"
          itemClassName="relative px-4 pb-3 text-sm font-medium transition-colors whitespace-nowrap md:text-base"
          activeClassName="text-primary border-b-2 border-primary"
          inactiveClassName="text-gray-600 hover:text-gray-900"
          showCounts
        />

        {selectedPrimaryTab === 'open' && (
          <TagBar
            items={OPEN_FILTERS.map((filter) => ({
              id: filter.id,
              label: (
                <span className="inline-flex items-center gap-2">
                  {filter.id === 'recommended' && <Sparkles className="h-4 w-4" />}
                  {filter.id === 'all'
                    ? isZh ? '全部' : 'All'
                    : filter.id === 'applied'
                      ? isZh ? '已申请' : 'Applied'
                      : filter.id === 'recommended'
                        ? isZh ? '推荐' : 'Recommended'
                        : isZh ? '需要处理' : 'Action Needed'}
                </span>
              ),
              count: openFilterCount(filter.id),
            }))}
            activeId={selectedOpenFilter}
            onChange={(filter) => {
              setSelectedOpenFilter(filter);
              setCurrentPage(1);
            }}
            className="mb-8"
            innerClassName="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0"
            itemClassName="inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap"
            activeClassName="border-primary bg-primary/10 text-primary"
            inactiveClassName="border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900"
            showCounts
          />
        )}

        {hasError && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">{isZh ? '加载机会时出错' : 'Error loading opportunities'}</h3>
              <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
            </div>
          </div>
        )}

        {(isLoading || isShortlistsLoading) && (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {!isLoading && !isShortlistsLoading && visibleItems.length === 0 && (
          <div className="py-16 text-center">
            <div className="mb-4 inline-block rounded-full bg-gray-100 p-3">
              <ClipboardList className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">{isZh ? '暂无相关机会' : 'No opportunities found'}</h3>
            <p className="mx-auto max-w-md text-gray-600">
              {selectedPrimaryTab === 'open' &&
                (isZh ? '你目前没有进行中的申请或推荐个案。' : 'You do not have any open applications or recommendations right now.')}
              {selectedPrimaryTab === 'matched' &&
                (isZh ? '你目前还没有成功配对的机会。' : 'You do not have any matched opportunities yet.')}
              {selectedPrimaryTab === 'closed' &&
                (isZh ? '你目前还没有已关闭的机会。' : 'You do not have any closed opportunities yet.')}
            </p>
            {selectedPrimaryTab === 'open' && (
              <Link
                to="/cases"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2 font-medium text-white transition-colors hover:bg-primary-600"
              >
                {isZh ? '浏览个案' : 'Browse Cases'}
              </Link>
            )}
          </div>
        )}

        {!isLoading && !isShortlistsLoading && visibleItems.length > 0 && (
          <>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {paginatedItems.map((item) => (
                <ApplicationCard
                  key={item.itemKey}
                  item={item}
                  onOpenDetails={(itemKey, caseId) => {
                    setSelectedItemKey(itemKey);
                    setSelectedCaseId(caseId);
                  }}
                  onRequestWithdraw={setApplicationToWithdraw}
                  onRequestRespond={(shortlist, action) => {
                    setShortlistResponseTarget({ shortlist, action });
                  }}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={!!selectedCaseId}
        onClose={() => {
          setSelectedCaseId(null);
          setSelectedItemKey(null);
        }}
        title={
          caseDetailData?.case ? (
            <>
              {isZh ? '个案详情' : 'Class Details'}{' '}
              <span className="text-sm font-normal text-gray-300">
                ({caseDetailData.case.id} · {isZh ? '发布于' : 'Posted'}{' '}
                {new Date(caseDetailData.case.createdAt).toLocaleDateString()})
              </span>
            </>
          ) : (
            isZh ? '个案详情' : 'Class Details'
          )
        }
        maxWidth="max-w-3xl"
        footer={
          <span className="text-xs text-gray-400">
            {selectedItem
              ? `${selectedItem.sourceLabel} · ${selectedItem.progressLabel}`
              : isZh ? '机会详情' : 'Opportunity details'}
          </span>
        }
      >
        {isCaseDetailLoading ? (
          <div className="py-10 text-center text-gray-500">{isZh ? '正在加载个案详情...' : 'Loading case details...'}</div>
        ) : caseDetailData?.case ? (
          <CaseDetailBody caseData={caseDetailData.case} />
        ) : (
          <div className="py-10 text-center text-gray-500">{isZh ? '无法加载个案详情。' : 'Unable to load case details.'}</div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!applicationToWithdraw}
        onClose={() => setApplicationToWithdraw(null)}
        onConfirm={() => {
          if (applicationToWithdraw) {
            withdraw.mutate(applicationToWithdraw.id, {
              onSuccess: () => setApplicationToWithdraw(null),
            });
          }
        }}
        title={isZh ? '撤回申请' : 'Withdraw Application'}
        message={
          <p>
            {isZh
              ? `你确定要撤回${applicationToWithdraw?.case?.subject ? `「${localizeText(locale, applicationToWithdraw.case.subject)}」的` : ''}申请吗？撤回后，这个机会会从你的进行中列表移除。`
              : `Are you sure you want to withdraw your application${applicationToWithdraw?.case?.subject ? ` for ${applicationToWithdraw.case.subject}` : ''}? This opportunity will move out of your open list.`}
          </p>
        }
        confirmLabel={isZh ? '撤回' : 'Withdraw'}
        cancelLabel={isZh ? '取消' : 'Cancel'}
        confirmVariant="danger"
        isLoading={withdraw.isPending}
      />

      <ConfirmModal
        isOpen={!!shortlistResponseTarget}
        onClose={() => setShortlistResponseTarget(null)}
        onConfirm={() => {
          if (shortlistResponseTarget) {
            respondToShortlist.mutate(
              {
                shortlistId: shortlistResponseTarget.shortlist.id,
                request: { status: shortlistResponseTarget.action },
              },
              {
                onSuccess: () => setShortlistResponseTarget(null),
              }
            );
          }
        }}
        title={
          shortlistResponseTarget?.action === 'accepted'
            ? isZh ? '接受推荐个案' : 'Accept Recommended Case'
            : isZh ? '拒绝推荐个案' : 'Reject Recommended Case'
        }
        message={
          shortlistResponseTarget?.action === 'accepted' ? (
            <p>
              {isZh
                ? '接受这个推荐个案，表示你认为它适合你；但这并不代表个案已经分配给你。最终仍需等待家长确认，才会正式配对。'
                : 'By accepting this recommended case, you are confirming that it seems suitable for you. This does not mean the case has already been assigned to you. We still need the parent\'s confirmation before any final match is made.'}
            </p>
          ) : (
            <p>
              {isZh
                ? '你确定要拒绝这个推荐个案吗？我们会记录你对此不感兴趣，并继续为你寻找其他选择。'
                : 'Are you sure you want to reject this recommended case? We will mark that you are not interested and continue the matching process with other options.'}
            </p>
          )
        }
        confirmLabel={
          shortlistResponseTarget?.action === 'accepted' ? (isZh ? '确认接受' : 'Yes, Accept') : (isZh ? '确认拒绝' : 'Yes, Reject')
        }
        cancelLabel={isZh ? '取消' : 'Cancel'}
        confirmVariant={shortlistResponseTarget?.action === 'accepted' ? 'primary' : 'danger'}
        isLoading={respondToShortlist.isPending}
      />
    </div>
  );
};

export default MyApplicationsPage;
